import {
    PlusCircle,
    FileText,
    Package,
    User,
    LogOut,
    LayoutDashboard,
    Home,
    LogIn,
    X,
    HelpCircle,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
    { icon: PlusCircle, label: 'New Quote', href: '/new', primary: true },
    { icon: Home, label: 'Home', href: '/' },
    { icon: LayoutDashboard, label: 'Quotes', href: '/quotes' },
    { icon: FileText, label: 'Orders', href: '/orders' },
    { icon: Package, label: 'Parts', href: '/parts' },
];

import { useAuth } from '../auth/AuthProvider';
import logo from '../../assets/logo.png';

interface SidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

export function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }: SidebarProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { signOut, user } = useAuth();

    const filteredNavItems = user
        ? navItems
        : navItems.filter(item => item.label === 'New Quote');

    const handleNavClick = (href: string) => {
        // Force hard reload when leaving 'New Quote' page to prevent WebGL/Context freeze
        if (location.pathname === '/new' && href !== '/new') {
            window.location.href = href;
        } else {
            navigate(href);
        }
        if (onClose) onClose();
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
                    onClick={onClose}
                />
            )}

            <div className={cn(
                "fixed inset-y-0 left-0 z-50 bg-sidebar text-sidebar-foreground h-[100dvh] flex flex-col border-r border-sidebar-accent transition-all duration-300 transform lg:translate-x-0 lg:static lg:inset-auto",
                isOpen ? "translate-x-0" : "-translate-x-full",
                isCollapsed ? "w-64 lg:w-20" : "w-64 lg:w-64"
            )}>
                <div className={cn(
                    "pt-10 pb-6 px-6 flex flex-col items-center justify-center relative transition-all duration-300",
                    isCollapsed ? "lg:pt-8 lg:pb-4 lg:px-2" : "pt-10 pb-6 px-6"
                )}>
                    <Link to="/" className="hover:opacity-90 transition-opacity flex justify-center w-full">
                        <img 
                            src={logo} 
                            alt="Print My Parts" 
                            className={cn(
                                "transition-all duration-300 object-contain",
                                isCollapsed ? "h-6 w-auto lg:h-6" : "h-16 w-auto"
                            )} 
                        />
                    </Link>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors absolute right-4 top-4"
                        >
                            <X size={24} />
                        </button>
                    )}
                </div>

                <nav className={cn(
                    "flex-1 py-4 space-y-2 overflow-y-auto transition-all duration-300",
                    isCollapsed ? "px-2" : "px-4"
                )}>
                    {filteredNavItems.map((item) => {
                        const isActive = location.pathname === item.href;
                        return (
                            <button
                                key={item.label}
                                onClick={() => handleNavClick(item.href)}
                                className={cn(
                                    "w-full flex items-center rounded-lg transition-all duration-300",
                                    isCollapsed 
                                        ? "lg:justify-center lg:px-0 lg:py-2.5 lg:gap-0" 
                                        : "gap-3 px-3 py-2",
                                    item.primary
                                        ? "bg-brand/10 text-brand hover:bg-brand/20 mb-6"
                                        : isActive
                                            ? "bg-sidebar-accent text-white"
                                            : "hover:bg-sidebar-accent text-slate-400 hover:text-white"
                                )}
                                title={isCollapsed ? item.label : undefined}
                            >
                                <item.icon size={20} className="shrink-0" />
                                <span className={cn(
                                    "font-medium transition-opacity duration-300 whitespace-nowrap",
                                    isCollapsed ? "lg:hidden" : "inline"
                                )}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </nav>

                <div className={cn(
                    "pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-sidebar-accent space-y-2 transition-all duration-300",
                    isCollapsed ? "p-2" : "p-4"
                )}>
                    <a
                        href="https://printmyparts.io/help-center"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                            "flex items-center text-slate-400 hover:text-white transition-colors",
                            isCollapsed ? "lg:justify-center lg:px-0 lg:py-2.5 lg:gap-0" : "gap-3 px-3 py-2"
                        )}
                        title={isCollapsed ? "Help" : undefined}
                    >
                        <HelpCircle size={20} className="shrink-0" />
                        <span className={cn(
                            "font-medium transition-opacity duration-300 whitespace-nowrap",
                            isCollapsed ? "lg:hidden" : "inline"
                        )}>
                            Help
                        </span>
                    </a>

                    {user ? (
                        <>
                            <Link
                                to="/account"
                                onClick={onClose}
                                className={cn(
                                    "flex items-center text-slate-400 hover:text-white transition-colors",
                                    isCollapsed ? "lg:justify-center lg:px-0 lg:py-2.5 lg:gap-0" : "gap-3 px-3 py-2"
                                )}
                                title={isCollapsed ? "Account" : undefined}
                            >
                                <User size={20} className="shrink-0" />
                                <span className={cn(
                                    "font-medium transition-opacity duration-300 whitespace-nowrap",
                                    isCollapsed ? "lg:hidden" : "inline"
                                )}>
                                    Account
                                </span>
                            </Link>
                            <button
                                onClick={() => {
                                    signOut();
                                    if (onClose) onClose();
                                }}
                                className={cn(
                                    "w-full flex items-center text-slate-400 hover:text-white transition-colors",
                                    isCollapsed ? "lg:justify-center lg:px-0 lg:py-2.5 lg:gap-0" : "gap-3 px-3 py-2"
                                )}
                                title={isCollapsed ? "Logout" : undefined}
                            >
                                <LogOut size={20} className="shrink-0" />
                                <span className={cn(
                                    "font-medium transition-opacity duration-300 whitespace-nowrap",
                                    isCollapsed ? "lg:hidden" : "inline"
                                )}>
                                    Logout
                                </span>
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/login"
                            onClick={onClose}
                            className={cn(
                                "flex items-center text-brand hover:text-brand/80 transition-colors font-bold",
                                isCollapsed ? "lg:justify-center lg:px-0 lg:py-2.5 lg:gap-0" : "gap-3 px-3 py-2"
                            )}
                            title={isCollapsed ? "Sign In" : undefined}
                        >
                            <LogIn size={20} className="shrink-0" />
                            <span className={cn(
                                "font-medium transition-opacity duration-300 whitespace-nowrap",
                                isCollapsed ? "lg:hidden" : "inline"
                            )}>
                                Sign In
                            </span>
                        </Link>
                    )}

                    {onToggleCollapse && (
                        <button
                            onClick={onToggleCollapse}
                            className={cn(
                                "w-full flex items-center text-slate-400 hover:text-white transition-colors border-t border-sidebar-accent/50 pt-3 mt-2 cursor-pointer",
                                isCollapsed ? "lg:justify-center lg:px-0 lg:py-2.5 lg:gap-0" : "gap-3 px-3"
                            )}
                            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                        >
                            {isCollapsed ? <ChevronRight size={20} className="shrink-0" /> : <ChevronLeft size={20} className="shrink-0" />}
                            <span className={cn(
                                "font-medium transition-opacity duration-300 whitespace-nowrap",
                                isCollapsed ? "lg:hidden" : "inline"
                            )}>
                                Collapse
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}



